// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import Icon from 'sail/Icon';
import {ButtonLink, Button} from 'sail/Button';
import {Text, BodyTextProps} from 'sail/Text';
import {DocsIcon, CardGroupQuickLinks} from 'docs/components';
import {AlignBox} from 'sail/Align';
import './HeroSection.scss';
import {getProductOverviewPageRoute, getProductDisplayName} from 'docs/util';
import {UnstyledLink} from 'sail/UnstyledLink';

const ProductHeader = ({badges}) => {
  if (!badges) {
    return null;
  }

  if (badges.length === 1) {
    // return product Icon + name
    return (
      <AlignBox reference={BodyTextProps}>
        <UnstyledLink href={getProductOverviewPageRoute(badges[0])}>
          <Group direction="horizontal">
            <DocsIcon size={20} category="product" icon={badges[0]} />
            <Box style={{paddingLeft: '6px'}} padding={{bottom: 20}}>
              <Text
                color="gray400"
                size={12}
                weight="medium"
                style={{textTransform: 'uppercase'}}
              >
                {getProductDisplayName(badges[0])}
              </Text>
            </Box>
          </Group>
        </UnstyledLink>
      </AlignBox>
    );
  } else if (badges.length > 1) {
    // return just the icons separated by a +
    return (
      <AlignBox reference={BodyTextProps}>
        <Box style={{paddingLeft: '6px'}} padding={{bottom: 20}}>
          <Group direction="horizontal" spacing={8}>
            <UnstyledLink href={getProductOverviewPageRoute(badges[0])}>
              <DocsIcon size={20} category="product" icon={badges[0]} />
            </UnstyledLink>
            <Icon icon="add" size={8} margin={{top: 4}} />
            <UnstyledLink href={getProductOverviewPageRoute(badges[1])}>
              <DocsIcon size={20} category="product" icon={badges[1]} />
            </UnstyledLink>
          </Group>
        </Box>
      </AlignBox>
    );
  } else {
    return null;
  }
};

type HeroSectionProps = {|
  children: React.Node,
  title: string,
  linkHref: string,
  linkLabel: string,
  imageSrc: string,
  // TODO change type to `typeof ProductNames`
  productBadge?: string,
  productBadges?: Array<string>,
  quickLinks?: Array<{|label: string, href: string|}>,
  useButton?: boolean,
|};

export const HeroSection = ({
  children,
  title,
  linkHref,
  linkLabel,
  imageSrc,
  productBadge,
  productBadges,
  useButton,
  quickLinks,
}: HeroSectionProps) => {
  const button = useButton ? (
    <Button
      icon={'arrowRight'}
      iconPosition="right"
      label={linkLabel}
      href={linkHref}
      size="medium"
      color="blue"
    />
  ) : (
    <ButtonLink
      icon={'arrowRight'}
      iconPosition="right"
      color="blue"
      label={linkLabel}
      size="large"
      href={linkHref}
    />
  );

  return (
    <Box
      className="HeroSection"
      flex={{direction: 'row', alignItems: 'center'}}
      divider={{color: 'light', width: 1, side: 'bottom'}}
      margin={{bottom: 16}}
    >
      <Box padding={{right: 20}} flexGrow={1}>
        <ProductHeader badges={productBadge ? [productBadge] : productBadges} />
        <Text size={24} weight="bold" color="dark">
          {title}
        </Text>
        <Box className="HeroSection-text" padding={{bottom: 20, right: 32}}>
          {children}
        </Box>
        {button}
        {quickLinks && (
          <Box margin={{top: 24}}>
            <CardGroupQuickLinks links={quickLinks} />
          </Box>
        )}
      </Box>
      <Box
        flexGrow={1}
        flex={{justifyContent: 'flex-end'}}
        style={{alignSelf: 'flex-end'}}
        className="HeroSection-right"
      >
        <img className="HeroSection-right-image" alt="" src={imageSrc} />
      </Box>
    </Box>
  );
};
